/**
 * Shared catalog vocabulary for the merchant screens (grades, conditions,
 * stock states) and small display helpers. Pure: no I/O.
 */

export { GRADES } from '@/lib/conditions';
export const STOCK_STATES = ['in_stock', 'reserved', 'sold', 'returned'];
/** Hand-made stock moves the server allows (offline sale and its undo); the rest follow orders, returns and inspections. */
export const MANUAL_STOCK = { in_stock: ['sold'], sold: ['in_stock'] };
export const LISTING_STATUSES = ['draft', 'published', 'unpublished'];

export const labelOf = (value) => String(value || '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
export const optionsOf = (values) => values.map((v) => ({ value: v, label: labelOf(v) }));

/** "Dell Latitude 7420" from a ProductModel row (tolerates nulls). */
export function modelName(model) {
  if (!model) return '—';
  return [model.brand, model.series, model.model_name].filter(Boolean).join(' ');
}

/** "Core i5 11th Gen · 16 GB · 512 GB SSD · 14\"" */
export function modelSpecs(model) {
  if (!model) return '';
  const cpu = [model.cpu_family, model.cpu_generation].filter(Boolean).join(' ');
  const ram = model.ram_gb ? `${model.ram_gb} GB` : '';
  const storage = model.storage_gb ? `${model.storage_gb} GB ${model.storage_type || ''}`.trim() : '';
  const display = model.display_size ? `${Number(model.display_size)}"` : '';
  return [cpu, ram, storage, display].filter(Boolean).join(' · ');
}

/** Suggested public listing title for a unit of a model. */
export function suggestTitle(model) {
  if (!model) return '';
  const cpu = model.cpu_family || '';
  const mem = [model.ram_gb && `${model.ram_gb}GB`, model.storage_gb && `${model.storage_gb}GB ${model.storage_type || ''}`.trim()].filter(Boolean).join(' / ');
  return [modelName(model), [cpu, mem].filter(Boolean).join(', ')].filter(Boolean).join(' — ');
}

/** Whole days between an ISO timestamp and `now`. */
export function ageInDays(iso, now = Date.now()) {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 0;
  return Math.max(0, Math.floor((now - t) / 86400000));
}

/**
 * The KORM door returns a created row in a few shapes depending on the action
 * ({ data: [row] }, [row], or a spread array { 0: row }). Pick the first row.
 */
export function firstRow(res) {
  if (!res) return null;
  if (Array.isArray(res)) return res[0] || null;
  if (Array.isArray(res.data)) return res.data[0] || null;
  if (res.data && typeof res.data === 'object') return res.data;
  if (res[0] && typeof res[0] === 'object') return res[0];
  if (res.id != null) return res;
  return null;
}

/** Rows from a KORM list response. */
export const rowsOf = (res) => (Array.isArray(res) ? res : (res && res.data) || []);

/** Case-insensitive match of a search term against a unit + its model. */
export function unitMatches(u, term) {
  const t = String(term || '').trim().toLowerCase();
  if (!t) return true;
  const m = u.ProductModel || {};
  return [u.internal_sku, u.serial_masked, m.brand, m.series, m.model_name, m.cpu_family, u.notes]
    .filter(Boolean).join(' ').toLowerCase().includes(t);
}
